import React from "react";
import { useWindowScroll } from "react-use";
import {
  styled,
  Theme,
  Hidden,
  Container,
  Box,
  Typography
} from "@material-ui/core";
import {
  TOP_BAR_HEIGHT,
  TOP_BAR_HEIGHT_MOBILE,
  MobileViewmodeType,
  StoreLabelType,
  GeolocationStateType
} from "../../constants";
import MobileViewmodeSwitcher from "./MobileViewmodeSwitcher";
import BackButton from "../../../../components/BackNavigation/BackButton";
import useTranslate from "../../../../hooks/use-translate";
import theme from "../../../../theme/material-ui-theme";

const Wrapper = styled("div")(({ theme }) => ({
  height: TOP_BAR_HEIGHT,
  [theme.breakpoints.down("sm")]: {
    height: TOP_BAR_HEIGHT_MOBILE
  },
  width: "100%"
}));

const StyledBackButton = styled(BackButton)(({ theme }) => ({
  fontSize: "1.2rem",
  lineHeight: "1.6rem",
  letterSpacing: "1.5px",
  marginTop: theme.spacing(3.25),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down(`sm`)]: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5)
  }
}));

const FixedWrapper = styled(({ isSticky, ...props }) => <div {...props} />)(
  ({ theme, isSticky }: { theme: Theme; isSticky: boolean }) => ({
    height: TOP_BAR_HEIGHT,
    [theme.breakpoints.down("sm")]: {
      height: TOP_BAR_HEIGHT_MOBILE
    },
    background: theme.palette.background.default,
    width: "100%",
    position: isSticky ? "fixed" : "relative",
    top: 0,
    left: 0,
    zIndex: 1,
    display: "flex",
    flexDirection: "row",
    paddingTop: isSticky ? `env(safe-area-inset-top)` : "0px",
    boxSizing: "content-box",
    flexShrink: 0
  })
);

const DesktopContainer = styled(Container)(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  paddingBottom: theme.spacing(3.5),
  alignItems: "flex-start",
  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.05)"
}));

type Props = {
  labels: StoreLabelType[];
  mobileViewmode: MobileViewmodeType;
  onMobileViewmodeChange: (MobileViewmodeType) => void;
  geolocationState: GeolocationStateType;
  onGeolocationClick: () => void;
};

const TopBar = ({ mobileViewmode, onMobileViewmodeChange }: Props) => {
  // Sticky
  const { y } = useWindowScroll();
  const isSticky = y > 50;

  const t = useTranslate();

  return (
    <Wrapper>
      <FixedWrapper isSticky={isSticky}>
        {/* Desktop */}
        <Hidden smDown>
          <DesktopContainer maxWidth="xl">
            <Box display="flex" flexDirection="column">
              <StyledBackButton to="/shop">
                {t("storeLocator.backToShop").toUpperCase()}
              </StyledBackButton>
              <Typography variant="h5">Showing all stores</Typography>
            </Box>
          </DesktopContainer>
        </Hidden>

        {/* Mobile */}
        <Hidden mdUp>
          <Box display="flex" flexDirection="column" width="100%" height="100%">
            <Container
              style={{
                flex: 1,
                display: "flex",
                alignContent: "center",
                paddingBottom: theme.spacing(2)
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                width="100%"
                paddingLeft="4px"
              >
                <StyledBackButton to="/shop">
                  {t("storeLocator.backToShop").toUpperCase()}
                </StyledBackButton>
                <Typography variant="h5">Showing all stores</Typography>
              </Box>
            </Container>
            <MobileViewmodeSwitcher
              value={mobileViewmode}
              onChange={onMobileViewmodeChange}
            />
          </Box>
        </Hidden>
      </FixedWrapper>
    </Wrapper>
  );
};

export default React.memo(TopBar);
